import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { getVehicleChassisById, VEHICLE_WEAPONS } from "@/data/vehicles";
import { useForceStore } from "@/store/forceStore";
import type { ForceVehicle } from "@/types/force";
import { calculateVehicleCost } from "@/utils/points";

interface VehicleCardProps {
	vehicle: ForceVehicle;
}

export function VehicleCard({ vehicle }: VehicleCardProps) {
	const removeVehicle = useForceStore((s) => s.removeVehicle);
	const setMountWeapon = useForceStore((s) => s.setVehicleMountWeapon);

	const chassis = getVehicleChassisById(vehicle.chassisId);
	const cost = calculateVehicleCost(vehicle);

	if (!chassis) return null;

	return (
		<Card variant="outlined" className="space-y-3">
			{/* Header — responsive */}
			<div className="flex items-start justify-between gap-2">
				<div className="flex items-center gap-2 flex-wrap min-w-0">
					<h3 className="font-semibold text-text-primary">{chassis.name}</h3>
					<Badge variant="amber" size="sm">
						Vehicle
					</Badge>
					{chassis.notes && <span className="text-xs text-text-muted">{chassis.notes}</span>}
				</div>
				<div className="flex items-center gap-2 lg:gap-3 shrink-0">
					<span className="font-mono text-sm font-semibold text-accent-secondary">{cost}r</span>
					<Button variant="danger" size="sm" onClick={() => removeVehicle(vehicle.id)}>
						Remove
					</Button>
				</div>
			</div>

			{/* Included weapons */}
			{chassis.includedWeapons && chassis.includedWeapons.length > 0 && (
				<div className="text-xs text-text-muted">
					Included: {chassis.includedWeapons.join(", ")}
				</div>
			)}

			{/* Weapon mount slots — responsive: stack on mobile */}
			{chassis.mountSlots.length > 0 && (
				<div className="space-y-2">
					<div className="text-xs text-text-muted uppercase font-semibold">Weapon Mounts</div>
					{chassis.mountSlots.map((slot) => {
						const currentSelection = vehicle.mountSelections.find((ms) => ms.slotId === slot.id);
						const availableWeapons = VEHICLE_WEAPONS.filter((w) =>
							slot.allowedTypes.includes(w.mountType),
						);

						return (
							<div key={slot.id} className="flex flex-col gap-1 lg:flex-row lg:items-center lg:gap-2">
								<span className="text-xs text-text-secondary lg:w-24">{slot.id}</span>
								<select
									value={currentSelection?.weaponId ?? ""}
									onChange={(e) => setMountWeapon(vehicle.id, slot.id, e.target.value || null)}
									className="w-full lg:flex-1 bg-bg-tertiary text-text-primary border border-border-default rounded-md px-2 py-2 lg:py-1 text-xs outline-none focus:border-accent-primary cursor-pointer min-h-[44px] lg:min-h-0"
								>
									<option value="">-- None --</option>
									{availableWeapons.map((w) => (
										<option key={w.id} value={w.id}>
											{w.name} ({w.cost}r)
										</option>
									))}
								</select>
							</div>
						);
					})}
				</div>
			)}
		</Card>
	);
}
